import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import PageBackground from "@/components/PageBackground";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const initialToken = params.get("token") || "";
  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match");
    if (password.length < 6) return setError("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-16 relative">
      <PageBackground variant="mint-cyan" />
      <div className="glass-strong rounded-[28px] w-full max-w-md p-8 relative z-10 fade-up">
        <div className="pf-chip pf-chip-mint mx-auto w-fit">Set a new password</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-4 text-center text-slate-900">Reset password</h1>
        <p className="text-sm text-slate-600 mt-2 text-center">Choose a new password for your Cyannect account.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
          {!initialToken && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Reset token</label>
              <input type="text" required value={token} onChange={(e) => setToken(e.target.value)} className="pf-input w-full px-4 py-3 text-sm font-mono" data-testid="reset-token" />
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">New password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="pf-input w-full pl-10 pr-4 py-3 text-sm" data-testid="reset-password" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Confirm password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pf-input w-full pl-10 pr-4 py-3 text-sm" data-testid="reset-confirm" />
            </div>
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50/70 backdrop-blur border border-red-200 rounded-xl px-4 py-2" data-testid="reset-error">{error}</div>}

          <button type="submit" disabled={loading || !token} className="pf-btn-primary w-full py-3 rounded-full font-semibold" data-testid="reset-submit">
            {loading ? "Updating..." : "Update password"}
          </button>
        </form>

        <div className="mt-6 text-sm text-slate-600 text-center">
          <Link to="/login" className="text-cyan-700 font-semibold hover:underline">Back to login</Link>
        </div>
      </div>
    </div>
  );
}
